import { Download, FailedDollar, MailStroke, RedoOutlined, SuccessDollar, TwofaStroke } from "@payping/icons";
import {
  Button,
  Col,
  Input,
  LegacyForm,
  Modal,
  Row,
  Typography,
  message,
  useMessagedAsyncFn,
  useMessagedAsyncRetry,
} from "@payping/web-components";
import { DashboardRoutes } from "@/router/constant";
import classnames from "classnames";
import { Trans } from "@payping/utils/localeUtils";
import { accountApis, requestApis } from "@payping/api";
import { useCallback, useState } from "react";
import { getUserPhoneNumber } from "@payping/redux/actions";
import { useAsyncFn } from "react-use";
import { useNavigate } from "react-router-dom";
import { useAppTranslation } from "@/utils/useAppTranslation";
import styles from "@/assets/scss/Settlement/Settlement.module.scss";
import { useAppDispatch, useAppSelector } from "@/utils/redux";
import { CreateStepEnum } from "@/pages/Settlement/Create/index";
import { useDownloadList } from "@/pages/Settlement/Create/downloadList";

interface Props {
  current?: number;
}

export const useCreateSettlementModals = ({ current }: Props) => {
  const { t } = useAppTranslation();
  const personalInfo = useAppSelector((state) => state.user.personalInfo);
  const { createdRequest, overview } = useAppSelector((state) => state.cashOuts);
  const [is2faEnabled, setIs2faEnabled] = useState<boolean>();
  const [modalVisible, setModalVisible] = useState(false);
  const [twoFAVisible, setTwoFAVisible] = useState(false);
  const [showCheck2faButton, setShowCheck2faButton] = useState(false);
  const [showGoogleAuthenticator, setShowGoogleAuthenticator] = useState(false);
  const [isClickGetCode, setIsClickGetCode] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [gAuthForm] = LegacyForm.useForm();
  const [twoFaForm] = LegacyForm.useForm();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { downloadList } = useDownloadList();

  const setTimer = useCallback(() => {
    let twoMin = 119;
    twoFaForm.setFieldsValue({ timer: twoMin });
    const interval = setInterval(() => {
      twoMin -= 1;
      if (twoMin < 1) clearInterval(interval);
      twoFaForm.setFieldsValue({ timer: twoMin.toString() });
    }, 1000);
  }, [twoFaForm]);
  const { loading: loadingData } = useMessagedAsyncRetry(
    async () => {
      if (current === CreateStepEnum.Verification) {
        setIs2faEnabled(overview?.authenticatorTotpEnabled);

        if (createdRequest && createdRequest.code) {
          await dispatch(getUserPhoneNumber());
        }
      }
    },
    [dispatch, current, overview?.authenticatorTotpEnabled, createdRequest],
    {
      success: false,
      useProgress: true,
    },
  );

  const [{ loading: onShowSmsModalLoading }, onShowSmsModal] = useAsyncFn(async () => {
    setModalVisible(false);
    setTwoFAVisible(true);
    setErrorText("");
    if (!Number(twoFaForm.getFieldValue("timer"))) {
      setTimer();
      await requestApis.sendTwoStepVerificationWithRequestCodePost(createdRequest?.code);
      message.success({ content: t("User:profile.personal.successSendCode") });
    }
  }, [createdRequest?.code, twoFaForm, setTimer, t]);

  const [{ loading: onOkAndSubmitLoading }, onOkAndSubmit] = useAsyncFn(async () => {
    setIsClickGetCode(true);
    if (is2faEnabled) {
      setModalVisible(false);
      setShowGoogleAuthenticator(true);
      setTwoFAVisible(false);
    } else await onShowSmsModal();
  }, [is2faEnabled, onShowSmsModal]);

  const [{ loading: loadingSubmit }, onSubmit] = useMessagedAsyncFn(
    async () => {
      try {
        await requestApis.submitRequestWithRequestCodeWithTwoStepCodeWithTwoStepTypePost(
          createdRequest?.code,
          twoFaForm.getFieldValue("twoStepCode"),
          0,
          {
            isRaw: true,
          },
        );
        setErrorText("");
      } catch (error: any) {
        if (error.status >= 500) {
          setErrorText(t("settlement.error.serverError"));
        } else {
          const contentType = error.headers.get("content-type");
          const errorMsg = contentType.includes("application/json") ? await error.json() : "";
          setErrorText(errorMsg.error);
        }
      } finally {
        setModalVisible(true);
        setTwoFAVisible(false);
        setShowGoogleAuthenticator(false);
        twoFaForm.resetFields();
      }
    },
    [createdRequest?.code, t, twoFaForm],
    undefined,
    { useProgress: true },
  );

  const [{ loading: loadingSubmitGoogle }, onSubmitGoogle] = useMessagedAsyncFn(
    async () => {
      try {
        await requestApis.submitRequestWithRequestCodeWithTwoStepCodeWithTwoStepTypePost(
          createdRequest?.code,
          gAuthForm.getFieldValue("twoStepCodegAuth"),
          1,
          {
            isRaw: true,
          },
        );
        setErrorText("");
      } catch (error: any) {
        if (error.status >= 500) {
          setErrorText(t("settlement.error.serverError"));
        } else {
          const contentType = error.headers.get("content-type");
          const errorMsg = contentType.includes("application/json") ? await error.json() : "";
          setErrorText(errorMsg.error);
        }
      } finally {
        setModalVisible(true);
        setTwoFAVisible(false);
        setShowGoogleAuthenticator(false);
        twoFaForm.resetFields();
      }
    },
    [createdRequest?.code, gAuthForm, t, twoFaForm],
    undefined,
    { useProgress: true },
  );

  const ModalsElement = (
    <>
      <Modal
        footer={null}
        open={twoFAVisible}
        width={400}
        centered
        onCancel={() => {
          setTwoFAVisible(false);
          twoFaForm.resetFields(["twoStepCode"]);
          gAuthForm.resetFields(["twoStepCodegAuth"]);
        }}
      >
        <div className="tw:text-center">
          <MailStroke width={100} height={100} className="tw:mx-auto tw:mb-4! tw:block" />
          <Typography.Text strong className="font-rem-15 tw:block">
            {t("settlement.twoFA.title")}
          </Typography.Text>
          <Typography.Text className="tw:my-4! tw:block tw:text-right">{t("settlement.twoFA.sendSMS")}</Typography.Text>
          {personalInfo && personalInfo.phoneVerified && personalInfo.phoneNumber && (
            <Typography.Text strong className={classnames(styles.letterSpace, "font-rem-12 tw:block tw:text-right")}>
              {`${personalInfo.phoneNumber.substring(0, 4)}xxxxx${personalInfo.phoneNumber.substring(8)}`}
            </Typography.Text>
          )}
          <LegacyForm form={twoFaForm} onFinish={onSubmit}>
            <LegacyForm.Item name="twoStepCode" rules={[{ required: true }]}>
              <Input placeholder={t("settlement.twoFA.sendSMSPlaceholder")} />
            </LegacyForm.Item>
            <Row justify="center">
              <LegacyForm.Item
                shouldUpdate={(prevValues, currentValues): boolean => prevValues.timer !== currentValues.timer}
              >
                {({ getFieldValue }) => {
                  const remaining = getFieldValue("timer");
                  if (Number(remaining) > 0) {
                    return (
                      <LegacyForm.Item name={"timer"} noStyle>
                        <Typography.Text>
                          {remaining} {t("second")}
                        </Typography.Text>
                      </LegacyForm.Item>
                    );
                  }
                  return (
                    <Button
                      size="small"
                      icon={<RedoOutlined width={14} height={14} />}
                      loading={loadingSubmitGoogle || onShowSmsModalLoading}
                      onClick={onShowSmsModal}
                      variant="link"
                    >
                      {t("sendAgain")}
                    </Button>
                  );
                }}
              </LegacyForm.Item>
            </Row>
            <Row justify="space-around">
              <Button
                type={"primary"}
                disabled={loadingData || loadingSubmit || loadingSubmitGoogle}
                loading={loadingData || loadingSubmit || loadingSubmitGoogle}
                htmlType={"submit"}
                block
                size={"large"}
              >
                {t("ok")}
              </Button>
            </Row>{" "}
            <Row justify="space-around">
              <Button
                variant="link"
                className="tw:mt-4!"
                disabled={loadingData || loadingSubmit || onShowSmsModalLoading || loadingSubmitGoogle}
                loading={loadingData || loadingSubmit || loadingSubmitGoogle}
                onClick={() => {
                  setShowGoogleAuthenticator(true);
                  setTwoFAVisible(false);
                }}
                block
                size={"small"}
              >
                {t("settlement.twoFA.useGoogleAuthenticator")}
              </Button>
            </Row>
          </LegacyForm>
        </div>
      </Modal>

      <Modal
        footer={null}
        open={showGoogleAuthenticator}
        width={400}
        centered
        onCancel={() => {
          setShowGoogleAuthenticator(false);
          twoFaForm.resetFields(["twoStepCode"]);
          gAuthForm.resetFields(["twoStepCodegAuth"]);
        }}
      >
        <div className="tw:text-center">
          <TwofaStroke width={100} height={100} className="tw:mx-auto tw:mb-4! tw:block" />
          <Typography.Text strong className="font-rem-15 tw:block">
            {t("settlement.twoFA.googleAuthenticatorTitle")}
          </Typography.Text>
          <Typography.Text className="tw:my-8! tw:block tw:text-right">
            {is2faEnabled ? (
              t("settlement.twoFA.gAuthDesc")
            ) : (
              <Trans i18nKey={t("settlement.twoFA.gAuthShouldEnable")} />
            )}
          </Typography.Text>
          <LegacyForm form={gAuthForm} onFinish={onSubmitGoogle}>
            {is2faEnabled ? (
              <>
                <LegacyForm.Item name="twoStepCodegAuth" rules={[{ required: true }]}>
                  <Input placeholder={t("settlement.twoFA.sendSMSPlaceholder")} />
                </LegacyForm.Item>
                <Row justify="space-around">
                  <Button
                    type={"primary"}
                    disabled={loadingData || loadingSubmit || loadingSubmitGoogle}
                    loading={loadingData || loadingSubmit || loadingSubmitGoogle}
                    htmlType={"submit"}
                    block
                    size={"large"}
                  >
                    {t("ok")}
                  </Button>
                </Row>
              </>
            ) : (
              <Row justify="space-around">
                <Button
                  type={"primary"}
                  block
                  size={"large"}
                  onClick={() => {
                    window.open(DashboardRoutes.profile["2fa"].url, "_blank");
                    setShowCheck2faButton(true);
                  }}
                >
                  {t("settlement.twoFA.enableGAuth")}
                </Button>
              </Row>
            )}

            {showCheck2faButton && !is2faEnabled && (
              <Row justify="space-around" className="tw:mt-4!">
                <Button
                  variant={"outlined"}
                  color="primary"
                  block
                  onClick={async () => {
                    const state = await accountApis.getTwoFactorAuthenticationStatusPut();
                    setIs2faEnabled(state);
                  }}
                >
                  {t("settlement.twoFA.enabled")}
                </Button>
              </Row>
            )}
            <Row justify="space-around">
              <Button
                variant="link"
                className="tw:mt-4!"
                disabled={loadingData || loadingSubmit || loadingSubmitGoogle}
                loading={loadingData || loadingSubmit || loadingSubmitGoogle}
                onClick={async () => {
                  setShowGoogleAuthenticator(false);
                  setTwoFAVisible(true);
                  if (!Number(twoFaForm.getFieldValue("timer"))) {
                    setTimer();
                    await requestApis.sendTwoStepVerificationWithRequestCodePost(createdRequest?.code);
                    message.success({ content: t("User:profile.personal.successSendCode") });
                  }
                }}
                block
                size={"small"}
              >
                {t("settlement.twoFA.useSMS")}
              </Button>
            </Row>
          </LegacyForm>
        </div>
      </Modal>
      <Modal
        footer={null}
        open={modalVisible}
        width={700}
        centered
        closable={false}
        onCancel={() => setModalVisible(false)}
      >
        <div className="tw:px-4! tw:pt-8! tw:text-center">
          {errorText ? (
            <FailedDollar width={100} height={100} className="tw:mx-auto tw:mb-8! tw:block" />
          ) : (
            <SuccessDollar width={100} height={100} className="tw:mx-auto tw:mb-8! tw:block" />
          )}
          <Typography.Text strong className="font-rem-15 tw:my-8! tw:block">
            {errorText || t("settlement.success")}
          </Typography.Text>
          <Row justify="space-around" className="tw:mt-12!">
            {errorText ? (
              <>
                <Col span={11}>
                  <Button
                    icon={<Download width={14} height={14} />}
                    loading={loadingData}
                    onClick={() => downloadList()}
                    className="tw:w-full"
                    size="large"
                    variant={"outlined"}
                    color="primary"
                  >
                    {t("downloadExcel")}
                  </Button>
                </Col>
                <Col span={11}>
                  <Button
                    onClick={onOkAndSubmit}
                    loading={loadingData || onOkAndSubmitLoading}
                    className="tw:w-full"
                    size="large"
                    type={"primary"}
                  >
                    {t("tryAgain")}
                  </Button>
                </Col>
              </>
            ) : (
              <Button
                onClick={() => navigate(DashboardRoutes.settlement.overview.url)}
                loading={loadingData}
                className="tw:w-full"
                size="large"
                type={"primary"}
              >
                {t("gotIt")}
              </Button>
            )}
          </Row>
        </div>
      </Modal>
    </>
  );

  return { ModalsElement, isClickGetCode, onOkAndSubmitLoading, onOkAndSubmit, loadingSubmit };
};
